import { useState } from 'react';
import {
  billingCarePlanRecord, billingElectionRecord, careMinutesLog, residentDiagnosisAdd,
  type ApiClient, type ElectionType, type PortalReadinessRow,
} from '@aithora/shared';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { sx } from '../../ui/designStyles';

// The Revenue Integrity fix sheet, restored from the production console (scorecard_view.dart):
// the readiness row's Fix writes the record the gate waits for, not a reminder to do it.
// Chronic diagnoses, a comprehensive care plan, the CCM/BHI election (all five CMS
// disclosures), and documented minutes. Not in the V3 design: the smallest dialog, on the
// existing button.

/** The readiness blockers that have a fix the sheet can write. */
export const FIXABLE = new Set(['chronic_dx', 'care_plan_goals', 'election_consent', 'minutes']);

const INPUT = 'box-sizing:border-box;width:100%;min-height:32px;padding:0 10px;border:1px solid rgba(34,39,46,.18);border-radius:8px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;margin-top:4px;';
const LABEL = 'display:block;font-size:14px;font-weight:600;color:#3C454F;margin-top:9px;';
const DISCLOSURES: [string, string][] = [
  ['service_availability', 'Their care team can help them between visits'],
  ['one_biller_per_month', 'Only one practitioner bills this each month'],
  ['cost_sharing', 'Cost sharing (coinsurance) may apply'],
  ['right_to_stop', 'They can stop at any time'],
  ['explained_and_accepted', 'It was explained and they said yes'],
];

const TITLE: Record<string, string> = {
  chronic_dx: 'Record a chronic condition',
  care_plan_goals: 'Complete the care plan',
  election_consent: 'Record the e-consent (election)',
  minutes: 'Log documented care minutes',
};

export function FixSheet({ client, row, onDone, onClose }: { client: ApiClient; row: PortalReadinessRow; onDone: () => void; onClose: () => void }) {
  const [f, setF] = useState({ icd10: '', desc: '', problems: '', goal: '', intervention: '', election: 'ccm_election' as ElectionType, witness: '', minutes: '', told: {} as Record<string, boolean> });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const id = row.residentId;
  const field = (label: string, key: 'icd10' | 'desc' | 'problems' | 'goal' | 'intervention' | 'witness' | 'minutes', ph: string) => (
    <label style={sx(LABEL)}>
      {label}
      <input type="text" aria-label={label} value={f[key]} placeholder={ph} onChange={(e) => set({ [key]: e.target.value } as Partial<typeof f>)} style={sx(INPUT)} />
    </label>
  );

  const body = (
    <>
      <span style={sx('display:block;font-size:16px;color:#3C454F;')}>{`${row.resident}: ${row.blockerLabel}. It is written to the resident's record and audited.`}</span>
      {row.blocker === 'chronic_dx' && (
        <>
          {field('ICD-10-CM code', 'icd10', 'e.g. E11.9')}
          {field('Description', 'desc', 'Type 2 diabetes')}
        </>
      )}
      {row.blocker === 'care_plan_goals' && (
        <>
          {field('Problems (ICD-10-CM, comma separated)', 'problems', 'E11.9, I10')}
          {field('Goal', 'goal', 'A1c under 8')}
          {field('Intervention', 'intervention', 'Weekly glucose review')}
        </>
      )}
      {row.blocker === 'election_consent' && (
        <>
          <label style={sx(LABEL)}>
            Program
            <select aria-label="Program" value={f.election} onChange={(e) => set({ election: e.target.value as ElectionType })} style={sx(INPUT)}>
              <option value="ccm_election">CCM (99490)</option>
              <option value="bhi_election">BHI (99484)</option>
            </select>
          </label>
          {DISCLOSURES.map(([k, words]) => (
            <label key={k} style={sx('display:flex;align-items:center;gap:8px;margin-top:8px;font-size:16px;color:#22272E;cursor:pointer;')}>
              <input type="checkbox" checked={!!f.told[k]} onChange={(e) => set({ told: { ...f.told, [k]: e.target.checked } })} />
              {words}
            </label>
          ))}
          {field('Witnessed by', 'witness', 'Name of the staff member present')}
        </>
      )}
      {row.blocker === 'minutes' && field('Documented minutes (CCM 99490)', 'minutes', 'e.g. 20')}
    </>
  );

  const run = async () => {
    if (row.blocker === 'chronic_dx') {
      await residentDiagnosisAdd(client, { params: { id }, body: { icd10: f.icd10.trim(), description: f.desc.trim() || undefined } });
    } else if (row.blocker === 'care_plan_goals') {
      await billingCarePlanRecord(client, {
        params: { id },
        body: {
          problems: f.problems.split(',').map((c) => c.trim()).filter(Boolean).map((icd10) => ({ icd10 })),
          goals: f.goal.trim() ? [{ description: f.goal.trim() }] : [],
          interventions: f.intervention.trim() ? [{ description: f.intervention.trim() }] : [],
        },
      });
    } else if (row.blocker === 'election_consent') {
      await billingElectionRecord(client, {
        params: { id },
        body: {
          consentType: f.election,
          disclosures: {
            service_availability: !!f.told.service_availability, one_biller_per_month: !!f.told.one_biller_per_month, cost_sharing: !!f.told.cost_sharing,
            right_to_stop: !!f.told.right_to_stop, explained_and_accepted: !!f.told.explained_and_accepted,
          },
          witnessedBy: f.witness.trim() || undefined,
          confirm: true,
        },
      });
    } else if (row.blocker === 'minutes') {
      await careMinutesLog(client, { params: { id }, body: { cptCode: '99490', minutes: Number(f.minutes) } });
    }
    onDone();
  };

  return <ConfirmDialog title={TITLE[row.blocker] ?? 'Fix'} body={body} confirmLabel="Save" onConfirm={run} onClose={onClose} />;
}
